import React from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { type IChildAccount } from '../../interfaces/IChildAccount';

interface CopyAccountsButtonProps {
  allAccounts: IChildAccount[];
  unallocatedAccounts: IChildAccount[];
  showOnlyUnallocated: boolean;
  organizationAccount?: string;
}

const CopyAccountsButton: React.FC<CopyAccountsButtonProps> = ({
  allAccounts,
  unallocatedAccounts,
  showOnlyUnallocated,
  organizationAccount,
}) => {
  const copyAccountsToClipboard = () => {
    const csvHeader = ['Account ID', 'Status', 'Assigned To', 'URL'].join(',');
    // Determine which accounts to copy based on filter state
    const accountsToCopy = showOnlyUnallocated ? unallocatedAccounts : allAccounts;

    const csvRows = accountsToCopy.map((account) => {
      const accountUrl = `https://${
        organizationAccount ? organizationAccount.split(/[-.]/, 1)[0].toLowerCase() + '-' : ''
      }${account.identifier.toLowerCase()}.snowflakecomputing.com`;

      // Include the allocation information (user) or "Unassigned" if not allocated
      const assignedTo = account.allocated_to ?? 'Unassigned';

      return [account.identifier, account.status, assignedTo, accountUrl].join(',');
    });

    const csvContent = [csvHeader, ...csvRows].join('\n');

    navigator.clipboard
      .writeText(csvContent)
      .then(() => {
        const message = showOnlyUnallocated
          ? `${accountsToCopy.length} unallocated accounts copied to clipboard as CSV`
          : `${accountsToCopy.length} accounts copied to clipboard as CSV`;
        alert(message);
      })
      .catch((err) => {
        console.error('Failed to copy to clipboard:', err);
        alert('Failed to copy to clipboard. See console for details.');
      });
  };

  return (
    <button
      onClick={copyAccountsToClipboard}
      className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors"
      title="Copy accounts as CSV"
    >
      <ClipboardDocumentIcon className="h-4 w-4" />
      Copy as CSV
    </button>
  );
};

export default CopyAccountsButton;
